import React from "react";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import { getCiReportDowHeatmap } from "../../../api/competitiveIntelligence";
import ThinScroll from "../../shared/ThinScroll";
import { PanelTakeaway, fmtRateWhole, DOW_COLUMNS } from "../shared";

// For each row, scale cell intensity to that property's own min/max (per spec —
// rows are independently normalized so we surface where each property charges
// its premium, regardless of absolute price level).
const cellShade = (value, rowMin, rowMax, isClient) => {
  if (value == null || rowMax === rowMin) {
    return { background: isClient ? "rgba(50, 177, 255, 0.06)" : "rgba(156, 163, 175, 0.04)" };
  }
  const fraction = (value - rowMin) / (rowMax - rowMin);
  // Indigo ramp matches the existing CI palette guidance (heatmap uses indigo).
  const alpha = 0.08 + fraction * 0.55;
  return { background: `rgba(99, 102, 241, ${alpha.toFixed(3)})` };
};

const DowHeatmapBody = ({ rows }) => {
  if (!rows.length) {
    return <PanelEmpty message="No DOW heatmap data for the selected category." />;
  }

  return (
    <ThinScroll className="overflow-x-auto">
      <table className="w-full min-w-[640px] table-fixed text-sm">
        <colgroup>
          <col style={{ width: "28%" }} />
          {DOW_COLUMNS.map((day) => (
            <col key={day.key} style={{ width: "9%" }} />
          ))}
          <col style={{ width: "9%" }} />
        </colgroup>
        <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800">
            <th className="px-3 py-2.5 text-left font-semibold sticky left-0 bg-gray-50 dark:bg-gray-800 z-10">Property</th>
            {DOW_COLUMNS.map((day) => (
              <th key={day.key} className="px-3 py-2.5 text-center font-semibold">{day.short}</th>
            ))}
            <th className="px-3 py-2.5 text-right font-semibold">Avg</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const values = DOW_COLUMNS.map((day) => row[day.key]).filter((rate) => rate != null);
            const rowMin = values.length ? Math.min(...values) : 0;
            const rowMax = values.length ? Math.max(...values) : 0;
            const rowAvg = values.length ? values.reduce((acc, rate) => acc + rate, 0) / values.length : null;
            return (
              <tr
                key={`${row.property_name}-${index}`}
                className={`border-b dark:border-gray-800 transition ${row.is_client ? "bg-sky-50 dark:bg-gray-800" : "hover:bg-gray-50 dark:hover:bg-gray-800/50"}`}
              >
                <td className={`px-3 py-2.5 whitespace-nowrap truncate sticky left-0 z-10 ${
                  row.is_client
                    ? "font-semibold text-[#32B1FF] bg-sky-50 dark:bg-gray-800"
                    : "text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-900"
                }`}>
                  {row.is_client ? "YOU" : (row.property_name || "—")}
                </td>
                {DOW_COLUMNS.map((day) => {
                  const rate = row[day.key];
                  const style = cellShade(rate, rowMin, rowMax, row.is_client);
                  const isPeak = rate != null && rate === rowMax;
                  return (
                    <td key={day.key} className="px-1 py-1 text-center" style={style}>
                      <div className={`tabular-nums text-sm py-1.5 ${isPeak ? "font-bold text-gray-900 dark:text-gray-100" : "text-gray-700 dark:text-gray-200"}`}>
                        {fmtRateWhole(rate)}
                      </div>
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  {rowAvg == null ? "—" : `$${rowAvg.toFixed(2)}`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="px-4 py-2 text-[11px] text-gray-500 dark:text-gray-400">
        Cell shade is normalized per row — darker = higher rate vs that property's average. Bold = peak day.
      </div>
    </ThinScroll>
  );
};

const DowHeatmapPanel = ({ clientId, categoryCode, say }) => (
  <AnalysisPanel
    id={`dow-heatmap-${clientId}-${categoryCode || "all"}`}
    title="Day-of-Week Heatmap"
    tip="Avg rate per day of week for you and each competitor. Each row is normalized to its own range so darker cells = that property's highest-priced days. Useful for spotting weekend/midweek pricing patterns."
    description="Average rate by day of week per property. Each row's intensity is normalized to that property's own range."
    badge={categoryCode ? categoryCode.toUpperCase() : null}
    fetcher={() => getCiReportDowHeatmap(clientId, { categoryCode: categoryCode || "rv" })}
    deps={[clientId, categoryCode]}
  >
    {({ data }) => {
      const rows = Array.isArray(data) ? data : [];
      // Client first, then alphabetical.
      const sorted = [...rows].sort((rowA, rowB) => {
        if (!!rowB.is_client !== !!rowA.is_client) return (!!rowB.is_client) - (!!rowA.is_client);
        return (rowA.property_name || "").localeCompare(rowB.property_name || "");
      });
      return (
        <>
          <PanelTakeaway panelKey="dow_heatmap" say={say} />
          <DowHeatmapBody rows={sorted} />
        </>
      );
    }}
  </AnalysisPanel>
);

export default DowHeatmapPanel;
